import { useEffect, useMemo, useState } from 'react';
import {
	getSignatureModelBox,
	hasVisibleSignatureContent,
	measureRenderedSignatureHtml,
	normalizeSignatureHtmlForRender,
	SIGNATURE_EDITOR_CANVAS_HEIGHT_PX,
	SIGNATURE_PREVIEW_WIDTH_PX,
} from './signatureLayoutShared';

interface SignaturePreviewProps {
	html: string;
}

export function SignaturePreview({ html }: SignaturePreviewProps) {
	const [displayHtml, setDisplayHtml] = useState('');
	const [actualSize, setActualSize] = useState<{ width: number; height: number }>({ width: 722, height: 260 });

	useEffect(() => {
		if (!html || html === '<p></p>' || !hasVisibleSignatureContent(html)) {
			setDisplayHtml('');
			return;
		}

		setDisplayHtml(normalizeSignatureHtmlForRender(String(html)));
	}, [html]);

	useEffect(() => {
		if (!displayHtml) {
			setActualSize({ width: 722, height: 260 });
			return;
		}

		setActualSize(measureRenderedSignatureHtml(displayHtml));
	}, [displayHtml]);

	const signatureBox = useMemo(() => getSignatureModelBox(html || displayHtml), [displayHtml, html]);
	const scale = useMemo(() => SIGNATURE_PREVIEW_WIDTH_PX / Math.max(actualSize.width, 1), [actualSize.width]);
	const previewHeight = useMemo(() => SIGNATURE_EDITOR_CANVAS_HEIGHT_PX, []);

	return (
		<div className="border-2 border-violet-200 rounded-xl p-4 bg-gradient-to-b from-violet-50 to-white flex flex-col">
			{displayHtml ? (
				<div className="rounded-xl border border-violet-200 bg-white/90 p-3 shadow-inner flex-1">
					<div
						className="relative bg-white rounded-lg border-2 border-dashed border-violet-500 shadow-sm mx-auto"
						style={{ width: `${SIGNATURE_PREVIEW_WIDTH_PX}px`, height: `${previewHeight}px` }}
					>
						<div className="absolute inset-0 pointer-events-none border border-violet-200 rounded" />
						{signatureBox ? (
							<div
								className="absolute pointer-events-none z-10 border border-dashed border-amber-400 bg-amber-50/20 rounded-sm"
								style={{
									left: `${signatureBox.x * scale}px`,
									top: `${signatureBox.y * scale}px`,
									width: `${signatureBox.width * scale}px`,
									height: `${signatureBox.height * scale}px`,
								}}
							/>
						) : null}
						<div
							className="origin-top-left overflow-visible"
							style={{
								width: `${actualSize.width}px`,
								height: `${actualSize.height}px`,
								transform: `scale(${scale})`,
								transformOrigin: 'top left',
							}}
							dangerouslySetInnerHTML={{ __html: displayHtml }}
						/>
					</div>
				</div>
			) : (
				<div className="text-center text-gray-400 py-10 border-2 border-dashed border-violet-300 rounded-lg bg-white/80 flex-1 flex items-center justify-center">
					No signature content yet. Upload an image to replace this placeholder instantly.
				</div>
			)}
		</div>
	);
}
